/**
 * @fileoverview JobNotesFeed — thin container wiring the shared `NotesFeedView`
 * to the job notes endpoints (`api.jobs.notes.*`).
 *
 * Mirrors `TaskNotesFeed` and `ContactNotesFeed`, giving a job the same
 * chronological journal (post / edit / delete, author-only editing,
 * newest-first). No AI summary surface (`showSummary={false}`): a job's notes
 * are read alongside the rest of the panel rather than digested.
 *
 * Used by: src/components/pre-appointment/pipeline-job-panel.tsx
 */

import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { NotesFeedView } from "@/components/contacts/notes/NotesFeedView";
import type { FeedNote } from "@/components/contacts/notes/NoteRow";

interface JobNotesFeedProps {
  jobId: Id<"jobs">;
  /** Read-only when the caller has no write access to the job. */
  readOnly?: boolean;
}

export function JobNotesFeed({ jobId, readOnly = false }: JobNotesFeedProps) {
  const { data: notes, isLoading } = useQuery({
    ...convexQuery(api.jobs.notes.listJobNotes, { jobId }),
  });

  // Identity of the calling user — decides which rows are editable.
  const { data: currentUser } = useQuery({
    ...convexQuery(api.users.queries.getCurrentUser, {}),
  });

  const createNote = useConvexMutation(api.jobs.notes.createJobNote);
  const updateNote = useConvexMutation(api.jobs.notes.updateJobNote);
  const deleteNote = useConvexMutation(api.jobs.notes.deleteJobNote);

  const handlePost = useCallback(
    async (content: string) => {
      await createNote({ jobId, content });
    },
    [jobId, createNote],
  );

  const handleSaveNote = useCallback(
    async (noteId: string, content: string) => {
      await updateNote({ noteId: noteId as Id<"jobNotes">, content });
    },
    [updateNote],
  );

  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      await deleteNote({ noteId: noteId as Id<"jobNotes"> });
    },
    [deleteNote],
  );

  const notesList: Array<FeedNote> = useMemo(
    () =>
      (notes ?? []).map((n) => ({
        _id: n._id,
        content: n.content,
        createdBy: n.createdBy,
        createdAt: n.createdAt,
        updatedAt: n.updatedAt,
        authorDisplayName: n.authorDisplayName,
        lastEditedByName: n.lastEditedByName ?? undefined,
      })),
    [notes],
  );

  return (
    <NotesFeedView
      notes={notesList}
      notesCount={notesList.length}
      isLoading={isLoading}
      readOnly={readOnly}
      currentUserId={currentUser?._id}
      showSummary={false}
      placeholder="Add a note about this job — who you spoke to, what was agreed, anything worth remembering."
      onPost={handlePost}
      onSaveNote={handleSaveNote}
      onDeleteNote={handleDeleteNote}
    />
  );
}
